import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Users, Clock, Target } from 'lucide-react';
import StatsSection from '../components/StatsSection';

const AboutPage = () => {
  const values = [
    { icon: Award, title: 'Passion for Design', description: 'Every space we create is infused with our love for aesthetics and attention to detail.' },
    { icon: Users, title: 'Client-Centric Approach', description: 'Your vision, lifestyle, and preferences are at the heart of every design decision.' },
    { icon: Clock, title: 'Precision & Excellence', description: 'We maintain the highest standards in craftsmanship and execution.' },
    { icon: Target, title: 'Timeless Design', description: 'We create spaces that remain beautiful and functional for years to come.' },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center bg-[#2C2C2C]">
        <div className="absolute inset-0 opacity-30">
          <img src="https://images.unsplash.com/photo-1631679706909-1844bbd07221?w=1920&q=80" alt="About" className="w-full h-full object-cover" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 pt-40">
          <span className="text-[#D4956A] text-sm tracking-[0.2em] uppercase mb-4 block">ABOUT US</span>
          <h1 className="font-serif text-5xl md:text-6xl text-white mb-6">Crafting Spaces That <span className="text-[#D4956A] italic">Tell Your Story</span></h1>
          <p className="text-white/70 text-lg max-w-2xl">At InArch Design Studio, we believe that great design goes beyond aesthetics. It's about creating environments that resonate with the people who inhabit them.</p>
        </div>
      </section>

      {/* Stats */}
      <StatsSection />

      {/* Our Story */}
      <section className="py-24 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#B87333] text-sm tracking-[0.2em] uppercase mb-4 block">OUR JOURNEY</span>
              <h2 className="font-serif text-4xl md:text-5xl text-[#2C2C2C] mb-6">Spaces That Feel Like <span className="text-[#B87333] italic">Home</span></h2>
              <div className="w-16 h-1 bg-[#B87333] mb-8" />
              <p className="text-[#6B6B6B] leading-relaxed mb-6">
                At InArch Design Studio, we believe that great design goes beyond aesthetics. It's about creating environments that resonate with the people who inhabit them — spaces that feel like home from the very first moment.
              </p>
              <p className="text-[#6B6B6B] leading-relaxed mb-6">
                Founded in 2012, we have grown from a small design firm to one of Navi Mumbai's most trusted interior design studios, but our core philosophy remains unchanged: every space has a story waiting to be told.
              </p>
              <p className="text-[#6B6B6B] leading-relaxed">
                From cozy apartments to expansive villas, from boutique offices to hospitality spaces — our portfolio spans the full spectrum of interior design. This diverse experience enriches every project we undertake.
              </p>
            </div>
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1631679706909-1844bbd07221?w=800&q=80" alt="Our Studio" className="rounded-lg shadow-xl" />
              <div className="absolute -bottom-8 -left-8 bg-[#B87333] text-white p-8 rounded-lg">
                <div className="font-serif text-4xl mb-2">20+</div>
                <div className="text-sm tracking-wider">YEARS OF EXCELLENCE</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-24 bg-[#F5F0E8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#B87333] text-sm tracking-[0.2em] uppercase mb-4 block">OUR PHILOSOPHY</span>
            <h2 className="font-serif text-4xl md:text-5xl text-[#2C2C2C] mb-4">Beauty Meets <span className="text-[#B87333] italic">Functionality</span></h2>
            <p className="text-[#6B6B6B] max-w-3xl mx-auto">
              We don't just design spaces — we create environments that inspire, connect, and enhance daily living. Our approach balances aesthetic elegance with practical functionality, ensuring every design serves both the eye and the lifestyle.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white p-8 rounded-lg shadow-md text-center">
                <div className="w-16 h-16 bg-[#B87333]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <value.icon className="text-[#B87333]" size={28} />
                </div>
                <h3 className="font-serif text-xl text-[#2C2C2C] mb-3">{value.title}</h3>
                <p className="text-[#6B6B6B] text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-24 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#B87333] text-sm tracking-[0.2em] uppercase mb-4 block">OUR APPROACH</span>
            <h2 className="font-serif text-4xl md:text-5xl text-[#2C2C2C] mb-4">A Personal Journey, <span className="text-[#B87333] italic">Not Just a Project</span></h2>
            <p className="text-[#6B6B6B] max-w-3xl mx-auto">
              Every project at InArch begins with understanding — not just your spatial needs, but your dreams, routines, and the moments that matter most to you.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            {/* Left Image Column */}
            <div className="lg:col-span-5">
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1600489000022-c2086d79f9d4?w=800&q=80" 
                  alt="Modern Kitchen Design" 
                  className="w-full h-[500px] object-cover rounded-lg shadow-xl" 
                />
                <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-[#B87333] rounded-lg hidden lg:flex items-center justify-center">
                  <div className="text-center text-white">
                    <div className="font-serif text-4xl">400+</div>
                    <div className="text-sm tracking-wider">Happy Clients</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right Content Column */}
            <div className="lg:col-span-7">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Approach Steps */}
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <div className="w-10 h-10 bg-[#B87333]/10 rounded-full flex items-center justify-center mb-4">
                    <span className="text-[#B87333] font-serif text-lg">01</span>
                  </div>
                  <h4 className="font-medium text-[#2C2C2C] mb-2">Consultation</h4>
                  <p className="text-[#6B6B6B] text-sm">Comprehensive consultation to understand your vision and requirements</p>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <div className="w-10 h-10 bg-[#B87333]/10 rounded-full flex items-center justify-center mb-4">
                    <span className="text-[#B87333] font-serif text-lg">02</span>
                  </div>
                  <h4 className="font-medium text-[#2C2C2C] mb-2">Planning</h4>
                  <p className="text-[#6B6B6B] text-sm">Detailed space planning and conceptual designs tailored to you</p>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <div className="w-10 h-10 bg-[#B87333]/10 rounded-full flex items-center justify-center mb-4">
                    <span className="text-[#B87333] font-serif text-lg">03</span>
                  </div>
                  <h4 className="font-medium text-[#2C2C2C] mb-2">Selection</h4>
                  <p className="text-[#6B6B6B] text-sm">Material and finish selection with your input at every step</p>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <div className="w-10 h-10 bg-[#B87333]/10 rounded-full flex items-center justify-center mb-4">
                    <span className="text-[#B87333] font-serif text-lg">04</span>
                  </div>
                  <h4 className="font-medium text-[#2C2C2C] mb-2">Execution</h4>
                  <p className="text-[#6B6B6B] text-sm">Seamless execution with minimal disruption and post-completion support</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#2C2C2C]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl md:text-5xl text-white mb-6">Ready to Start <span className="text-[#D4956A] italic">Your Journey?</span></h2>
          <p className="text-white/70 text-lg mb-8">Every great space begins with a conversation. Let's discuss your vision and explore how we can bring it to life.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-copper px-8 py-3 inline-flex items-center gap-2 text-sm tracking-wider">
              GET IN TOUCH <ArrowRight size={16} />
            </Link>
            <Link to="/services" className="btn-outline-copper border-[#B87333] text-[#D4956A] hover:bg-[#B87333] hover:text-white px-8 py-3 inline-flex items-center gap-2 text-sm tracking-wider">
              OUR SERVICES <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
